import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CacheService } from '@api/custom-services/cache.service';
import { CompanySettingsDto, UpdateCompanySettingsDto } from '@api/models';
import { CompanyService } from '@api/services/company.service';
import { InputFieldComponent } from '@shared/components/input-field/input-field.component';
import { SubscriptionDirective } from '@shared/directives/subscriptions.directive';
import { Observable, tap } from 'rxjs';

@Component({
   selector: 'app-company-email',
   imports: [
      CommonModule,
      ReactiveFormsModule,
      MatCardModule,
      InputFieldComponent,
      MatFormFieldModule,
      MatInputModule,
      MatButtonModule,
      MatIconModule,
   ],
   templateUrl: './company-email.component.html',
   styleUrl: './company-email.component.scss',
})
export class CompanyEmailComponent extends SubscriptionDirective {
   private formBuilder = inject(FormBuilder);
   private companyService = inject(CompanyService);
   private cacheService = inject(CacheService);
   private _snackBar = inject(MatSnackBar);

   settings$!: Observable<CompanySettingsDto>;

   form = this.formBuilder.nonNullable.group({
      digitalFooter: [undefined as any as string],
      digitalHeader: [undefined as any as string],
      emailHeader: [undefined as any as string],
      emailPickupHeader: [undefined as any as string],
      emailFooter: [undefined as any as string],
      emailPickupFooter: [undefined as any as string],
      emailOrderConfirmation: [undefined as any as string],
      emailGuest: [undefined as any as string],
      emailFrom: [undefined as any as string],
      incognitoEmailFrom: [undefined as any as string],
      plans: this.formBuilder.array([]),
      emailVariables: this.formBuilder.array([]),
   });

   constructor() {
      super();
      this.initEdit();
   }

   initEdit() {
      this.settings$ = this.companyService.companyControllerGetCompanySettings().pipe(
         tap((settings) => {
            this.form.patchValue(settings);

            if (settings.plans) {
               settings.plans.forEach((item) => {
                  this.plans.push(this.buildPlanForm(item.name, item.priceStart, item.priceEnd));
               });
            }

            if (settings.emailVariables) {
               settings.emailVariables.forEach((item) => {
                  this.variables.push(this.buildVariableForm(item.name, item.value, item.incognitoValue));
               });
            }
         })
      );
   }

   setPlaceholder(
      control:
         | 'emailHeader'
         | 'emailFooter'
         | 'emailPickupHeader'
         | 'emailOrderConfirmation'
         | 'emailGuest'
         | 'emailPickupFooter'
         | 'digitalHeader'
         | 'digitalFooter',
      placeholder: string
   ) {
      const currentValue = this.form.controls[`${control}`].value;
      this.form.controls[`${control}`].setValue(`${currentValue} ${placeholder}`);
   }

   saveSettings() {
      const request$ = this.companyService.companyControllerUpdateMyCompanyEmailSettings({
         body: this.form.value as UpdateCompanySettingsDto,
      });

      this.subs = request$.subscribe({
         next: () => {
            this._snackBar.open('Settings was successfully saved.', 'OK');
         },
         complete: () => {
            this.cacheService.refresh$.company.next();
         },
      });
   }

   buildPlanForm(name: string = '', priceStart: number = 0, priceEnd: number = 0) {
      return this.formBuilder.nonNullable.group({
         name: [name, Validators.required],
         priceStart: [priceStart, Validators.required],
         priceEnd: [priceEnd, Validators.required],
      });
   }

   get plans() {
      return this.form.controls['plans'] as FormArray;
   }

   addPlan() {
      this.plans.push(this.buildPlanForm());
   }

   removePlan(index: number) {
      this.plans.removeAt(index);
   }

   buildVariableForm(name: string = '', value: string = '', incognitoValue: string = '') {
      return this.formBuilder.nonNullable.group({
         name: [name, Validators.required],
         value: [value, Validators.required],
         incognitoValue: [incognitoValue],
      });
   }

   addVariable() {
      this.variables.push(this.buildVariableForm());
   }

   removeVariable(index: number) {
      this.variables.removeAt(index);
   }

   get variables() {
      return this.form.controls['emailVariables'] as FormArray;
   }
}
